import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useIsMobile } from 'components/v3';
import { InfoTip } from '../../help';
import Glyph from '../Glyph';
import { ORANGE, sx } from '../sx';
import DialogShell, {
  DialogButton,
  Foot,
  FootNote,
  Label,
  LinkButton,
  Spacer,
} from './DialogShell';
import FieldChooser from './FieldChooser';

// Edit the active table view's columns, or create a new view (design
// sub_modals.jsx ColumnsModal). The built-in Default view keeps its name
// and can be reset; a saved view can be renamed or deleted.

const NameRow = styled.div`
  flex: none;
  display: flex;
  flex-direction: ${(p) => (p.$mobile ? 'column' : 'row')};
  align-items: ${(p) => (p.$mobile ? 'stretch' : 'center')};
  gap: ${(p) => (p.$mobile ? '8px' : '16px')};
  padding: ${(p) => (p.$mobile ? '0 16px 14px' : '0 24px 18px')};

  & > ${Label} {
    flex: none;
    width: ${(p) => (p.$mobile ? 'auto' : '110px')};
  }
`;

export const NameInput = styled.input`
  flex: 1;
  min-width: 0;
  height: ${(p) => (p.$sm ? '38px' : '44px')};
  box-sizing: border-box;
  padding: 0 14px;
  border-radius: 6px;
  border: 1px solid ${(p) => sx(p.theme).line2};
  background: ${(p) => sx(p.theme).field};
  color: ${(p) => sx(p.theme).text};
  font-size: ${(p) => (p.$sm ? '14px' : '16px')};
  font-weight: 700;
  outline: none;

  &::placeholder {
    color: ${(p) => sx(p.theme).disabled};
    font-weight: 500;
  }

  &:focus {
    border-color: ${ORANGE};
    box-shadow: 0 0 0 3px rgba(250, 70, 22, 0.2);
  }
`;

const StaticName = styled.div`
  flex: 1;
  min-width: 0;
  height: 44px;
  box-sizing: border-box;
  padding: 0 14px;
  border-radius: 6px;
  border: 1px solid ${(p) => sx(p.theme).line2};
  background: ${(p) => sx(p.theme).inset};
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => sx(p.theme).text};

  svg {
    color: ${ORANGE};
  }
`;

const Badge = styled.span`
  margin-left: auto;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${(p) => sx(p.theme).label};
`;

const Confirm = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => sx(p.theme).text2};
`;

const ColumnsModal = ({
  catalog,
  view,
  isNew,
  defaults,
  onSave,
  onDelete,
  onClose,
}) => {
  const isMobile = useIsMobile();
  const [name, setName] = useState(isNew ? '' : view.name);
  const [cols, setCols] = useState(view.cols.slice());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const nameRef = useRef(null);
  useEffect(() => {
    if (isNew && nameRef.current) nameRef.current.focus();
  }, [isNew]);

  const builtIn = !!view.builtIn && !isNew;
  const canSave = cols.length > 0 && (builtIn || name.trim().length > 0);
  const save = () => {
    if (canSave) onSave({ name: builtIn ? view.name : name.trim(), cols });
  };

  let left = null;
  if (!isNew && builtIn) {
    left = (
      <LinkButton onClick={() => setCols(defaults.slice())}>
        <Glyph name="rotate-ccw" size={14} />
        Reset to default
      </LinkButton>
    );
  } else if (!isNew && confirmDelete) {
    left = (
      <Confirm>
        <span>{`Delete “${view.name}”?`}</span>
        <DialogButton $sm $danger onClick={onDelete}>
          Delete
        </DialogButton>
        <DialogButton $sm onClick={() => setConfirmDelete(false)}>
          Keep
        </DialogButton>
      </Confirm>
    );
  } else if (!isNew) {
    left = (
      <LinkButton $danger onClick={() => setConfirmDelete(true)}>
        <Glyph name="trash-2" size={14} />
        Delete view
      </LinkButton>
    );
  }

  return (
    <DialogShell
      label={isNew ? 'New table view' : 'Edit columns'}
      icon="columns"
      title={isNew ? 'New table view' : 'Edit columns'}
      sub={
        isNew
          ? 'Pick the fields this view shows and the order they run in, then name it. It shows up as a tab above the table.'
          : `Choose the fields that show in “${view.name}” and the order they run in. Saved for this form.`
      }
      onClose={onClose}
    >
      <NameRow $mobile={isMobile}>
        <Label>
          {builtIn ? (
            <span>View name</span>
          ) : (
            <label htmlFor="fb-view-name">View name</label>
          )}
          <InfoTip
            title="Table views"
            text="Each view is a tab above the submissions table with its own columns. Switch between them any time."
          />
        </Label>
        {builtIn ? (
          <StaticName>
            <Glyph name="table" size={15} />
            {view.name}
            <Badge>Built-in</Badge>
          </StaticName>
        ) : (
          <NameInput
            id="fb-view-name"
            ref={nameRef}
            value={name}
            placeholder="e.g. Contact list"
            maxLength={40}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') save();
            }}
          />
        )}
      </NameRow>
      <FieldChooser
        catalog={catalog}
        value={cols}
        onChange={setCols}
        selTitle="Shown in table"
        emptyText="Pick at least one field to show."
      />
      <Foot $mobile={isMobile}>
        {left}
        {isMobile ? null : <Spacer />}
        {isMobile ? null : (
          <FootNote>{`${cols.length} ${
            cols.length === 1 ? 'column' : 'columns'
          }`}</FootNote>
        )}
        {isMobile ? null : (
          <DialogButton onClick={onClose}>Cancel</DialogButton>
        )}
        <DialogButton
          $primary
          $grow={isMobile}
          disabled={!canSave}
          onClick={save}
        >
          <Glyph name="check" size={16} />
          {isNew ? 'Create view' : 'Save view'}
        </DialogButton>
      </Foot>
    </DialogShell>
  );
};

ColumnsModal.propTypes = {
  catalog: PropTypes.arrayOf(PropTypes.object).isRequired,
  view: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    cols: PropTypes.arrayOf(PropTypes.string).isRequired,
    builtIn: PropTypes.bool,
  }).isRequired,
  isNew: PropTypes.bool,
  defaults: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

ColumnsModal.defaultProps = { isNew: false, onDelete: () => undefined };

export default ColumnsModal;
